import {
  cloneElement,
  useCallback,
  Fragment,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";
import { createPortal, flushSync } from "react-dom";
import { Link } from "react-router-dom";

import { floatingCeiling, floatingContainer, placeFloating, whenBoxRisen, type Align, type Side } from "@/lib/placeFloating";

import { MoreIcon } from "./icons";
import styles from "./Menu.module.css";

/**
 * Width of the icon column in px — the size an icon in a menu is drawn at.
 * An item without an icon keeps an empty column this wide (`.iconSlot` in
 * `Menu.module.css` says the same number) wherever another item has one.
 */
export const MENU_ICON_SIZE = 12;

/** Marks every trigger, so a press on another menu's trigger means „that one instead". */
const TRIGGER_ATTRIBUTE = "data-menu-trigger";

export interface MenuItem {
  key: string;
  label: string;
  /** A link: `<a role="menuitem">` — a `Link` when `internal`. */
  href?: string;
  /** `href` is an app route: rendered as a router `Link`. */
  internal?: boolean;
  /** A download: the link carries the `download` attribute (a string names the file). */
  download?: boolean | string;
  /** Runs after the menu has closed and the focus is back on the trigger. */
  onSelect?: () => void;
  /** Shown, announced as disabled, skipped by the arrow keys, not activatable. */
  disabled?: boolean;
  /**
   * A quieter second line under the label — why a held item is held (Task 9:
   * „In einem beendeten Stadium lässt sich nicht übergeben."). It describes
   * the item and is no part of its name.
   */
  hint?: string;
  /** The id of a sentence elsewhere on the page that says why — the waiting line under the Vorgang's head. */
  describedBy?: string;
  /** A rule above this item — before the gesture that changes things. Never above the first. */
  separatorBefore?: boolean;
  /** Leading. Where any item has one, the others keep an empty column of the same width. */
  icon?: ReactNode;
}

export interface TriggerProps {
  ref: Ref<HTMLButtonElement>;
  "aria-haspopup": "menu";
  "aria-expanded": boolean;
  "aria-controls"?: string;
  "aria-describedby"?: string;
  onClick: () => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLButtonElement>) => void;
  "data-menu-trigger": "";
}

interface MenuProps {
  /** The menu's `aria-label` — and the default trigger's. */
  label: string;
  /**
   * A quiet line over the items — whose menu it is: the account menu's name
   * (UX-Gutachten 2, B-5). Not an item; the label says it to a screen reader,
   * so the line is hidden from it.
   */
  heading?: string;
  items: readonly MenuItem[];
  /** Replaces the default 28-px button with three dots: spread `props` on one `<button>`. */
  trigger?: (props: TriggerProps) => ReactElement;
  /** What the trigger is described by — the row, the entry it belongs to. */
  describedBy?: string;
  /** Which edge of the trigger the menu lines up with. Default: the right one — it grows to the left. */
  align?: "start" | "end";
  /** The default trigger's size: `m` 28 px (default), `s` 24 px for a row that cannot spare more. A custom trigger sets its own. */
  size?: "s" | "m";
  /** Fired once the menu has closed, however it closed. */
  onClosed?: () => void;
}

type CloseReason = "escape" | "tab" | "select" | "outside" | "scroll" | "resize";

interface LayerClose {
  reason: CloseReason;
  /** The item that was chosen, for `select`. */
  item?: MenuItem;
  /** Whether the focus was inside the layer when it closed — it is about to lose it. */
  focusWithin: boolean;
}

/**
 * The click after a press that closed the menu was spent on closing it: it
 * must not open the row, or the link, under the pointer (as the stage
 * popover does, register fix round 4, I2). Capture phase on the window, so
 * neither React's handlers nor a link's navigation ever see it.
 *
 * Disarmed by the first of: that click, the next press (or a press that is
 * cancelled), the next key, 500 ms. A press that ends without a click (a
 * touch that turns into a scroll, a drag off the page) must not leave a trap
 * for a later, unrelated click, and a key's own synthetic click (Enter on a
 * button) is never swallowed. Whichever comes first removes the rest — no
 * listener and no timer outlives its purpose.
 */
function swallowNextClick() {
  const disarmers = ["pointerdown", "pointercancel", "keydown"] as const;
  function disarm() {
    window.clearTimeout(timer);
    window.removeEventListener("click", swallow, true);
    for (const type of disarmers) window.removeEventListener(type, disarm, true);
  }
  function swallow(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    disarm();
  }
  window.addEventListener("click", swallow, true);
  for (const type of disarmers) window.addEventListener(type, disarm, true);
  const timer = window.setTimeout(disarm, 500);
}

function enabledItems(layer: HTMLElement): HTMLElement[] {
  return Array.from(layer.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])'));
}

/**
 * The icon in its column. An element (the usual case: an icon from
 * `icons.tsx`) is the column itself — it gets the class; anything else is
 * wrapped in a span that is.
 */
function iconColumn(icon: ReactNode): ReactNode {
  if (isValidElement<{ className?: string }>(icon)) {
    return cloneElement(icon, { className: [icon.props.className, styles.icon].filter(Boolean).join(" ") });
  }
  return (
    <span aria-hidden="true" className={styles.icon}>
      {icon}
    </span>
  );
}

interface LayerProps {
  id?: string;
  label: string;
  heading?: string;
  items: readonly MenuItem[];
  /** Where it is rendered (`floatingContainer`). */
  container: HTMLElement;
  /** A trigger, measured when the layer is placed; or the pointer's point. */
  anchor: HTMLElement | { x: number; y: number };
  side: Side;
  align: Align;
  /** Which end of the list takes the focus on opening. */
  focus: "first" | "last";
  /** Whether the click after a press beside the layer is spent on closing it. */
  swallowOutsideClick: boolean;
  onClose: (close: LayerClose) => void;
}

/**
 * The layer: the `<ul role="menu">` in its portal, its placement, its keys,
 * and everything that closes it. `Menu` and `ContextMenu` differ in what they
 * hang it on and where the focus goes afterwards, not in how it behaves.
 *
 * Rendered hidden, measured in a layout effect and placed by `placeFloating`
 * before the first paint (the way `Tooltip` is). The size comes from
 * `offsetWidth`/`offsetHeight`, not from a bounding rectangle: the layer
 * starts at `scale(0.96)` (`@starting-style`) and a rectangle would be 4 %
 * too small. Parked at 0/0 it also tells where its containing block begins —
 * the viewport, unless it sits in a dialog's box, which is transformed while
 * it rises in — and that is subtracted. A menu opened in that window (the
 * rise is `--dur-4`) is placed against the box; when the box's animation ends
 * the same `left`/`top` would be read against the viewport, so the layer is
 * placed again then (`whenBoxRisen`).
 */
function Layer({
  id,
  label,
  heading,
  items,
  container,
  anchor,
  side,
  align,
  focus,
  swallowOutsideClick,
  onClose,
}: LayerProps) {
  const layerRef = useRef<HTMLUListElement>(null);
  const hintPrefix = useId();
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  // Closing moves the focus (to the trigger, to the opener), and a focus that
  // leaves the layer is itself a reason to close: answered once, not again
  // from inside the answer.
  const closingRef = useRef(false);
  const finish = useCallback((close: LayerClose) => {
    if (closingRef.current) return;
    closingRef.current = true;
    try {
      closeRef.current(close);
    } finally {
      closingRef.current = false;
    }
  }, []);

  const element = anchor instanceof HTMLElement ? anchor : null;
  const pointX = "x" in anchor ? anchor.x : 0;
  const pointY = "y" in anchor ? anchor.y : 0;

  // Park, measure, place — before the first paint, and again when the dialog
  // box the layer sits in has finished rising (see above).
  const place = useCallback(() => {
    const layer = layerRef.current;
    if (layer === null) return;
    layer.style.left = "0px";
    layer.style.top = "0px";
    // Scaling about the top left leaves the rectangle's corner where it is,
    // whatever scale the entrance has reached. A second pass (StrictMode, a
    // moved point, the box's rise ending) would otherwise find the origin
    // written by the first.
    layer.style.setProperty("transform-origin", "0 0");
    const origin = layer.getBoundingClientRect();
    const placed = placeFloating(
      element !== null ? element.getBoundingClientRect() : { x: pointX, y: pointY },
      { width: layer.offsetWidth, height: layer.offsetHeight },
      {
        width: document.documentElement.clientWidth || window.innerWidth,
        height: document.documentElement.clientHeight || window.innerHeight,
        // Never over the steel masthead (UX-Gutachten 2, B-1).
        top: floatingCeiling(element, container),
      },
      { side, align },
    );
    layer.style.left = `${placed.left - origin.left}px`;
    layer.style.top = `${placed.top - origin.top}px`;
    layer.style.setProperty("transform-origin", `${placed.originX}px ${placed.originY}px`);
    layer.dataset.side = placed.side;
    layer.dataset.placed = "true";
  }, [element, container, pointX, pointY, side, align]);

  useLayoutEffect(() => {
    place();
  }, [place]);

  useEffect(() => whenBoxRisen(container, place), [container, place]);

  // The focus goes in once the layer is visible — a hidden element takes none.
  useLayoutEffect(() => {
    const layer = layerRef.current;
    if (layer === null) return;
    const enabled = enabledItems(layer);
    const target = focus === "last" ? enabled[enabled.length - 1] : enabled[0];
    // Nothing to choose: the layer itself, so Escape still reaches it.
    (target ?? layer).focus({ preventScroll: true });
    // Without scrolling the page — but inside the layer, which is capped
    // (Task 14) and scrolls in itself: opened from the end of a long menu,
    // the item with the focus must be in view (WCAG 2.4.11; final review,
    // M-4). `nearest` moves the layer's own scroll as little as it takes.
    target?.scrollIntoView?.({ block: "nearest" });
  }, [focus]);

  // What closes it besides the keys.
  useEffect(() => {
    const layer = layerRef.current;
    if (layer === null) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (layer.contains(target) || element?.contains(target)) return;
      finish({ reason: "outside", focusWithin: false });
      if (swallowOutsideClick && !(target instanceof Element && target.closest(`[${TRIGGER_ATTRIBUTE}]`) !== null)) {
        swallowNextClick();
      }
    };
    // The layer is fixed and what it belongs to is not: once the page moves
    // under it, they part. A scroll inside the layer is its own.
    const onScroll = (event: Event) => {
      if (event.target instanceof Node && layer.contains(event.target)) return;
      finish({ reason: "scroll", focusWithin: layer.contains(document.activeElement) });
    };
    const onResize = () => finish({ reason: "resize", focusWithin: layer.contains(document.activeElement) });
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [element, swallowOutsideClick, finish]);

  function onKeyDown(event: ReactKeyboardEvent<HTMLUListElement>) {
    const layer = layerRef.current;
    if (layer === null) return;
    const enabled = enabledItems(layer);
    const here = enabled.indexOf(document.activeElement as HTMLElement);
    const go = (to: HTMLElement | undefined) => {
      event.preventDefault();
      to?.focus();
    };
    switch (event.key) {
      case "Escape":
        // Prevented, so a Dialog around it (which closes on any Escape nobody
        // handled) leaves itself alone: the menu goes first.
        event.preventDefault();
        finish({ reason: "escape", focusWithin: true });
        return;
      case "Tab":
        // Tab goes on from the trigger, as if the menu had never been there.
        event.preventDefault();
        finish({ reason: "tab", focusWithin: true });
        return;
      case "ArrowDown":
        go(enabled[(here + 1) % Math.max(enabled.length, 1)]);
        return;
      case "ArrowUp":
        go(enabled[here < 0 ? enabled.length - 1 : (here - 1 + enabled.length) % enabled.length]);
        return;
      case "Home":
        go(enabled[0]);
        return;
      case "End":
        go(enabled[enabled.length - 1]);
        return;
      case "Enter":
      case " ":
        // Followed by hand, for links and buttons alike: the click closes the
        // menu and moves the focus, and a native one would then arrive as
        // the key coming up on the trigger.
        if (here >= 0) {
          event.preventDefault();
          enabled[here]?.click();
        }
        return;
    }
    // Typeahead: the next enabled item, after the one with the focus, that
    // begins with the letter.
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const typed = event.key.toLocaleLowerCase("de");
      for (let step = 1; step <= enabled.length; step += 1) {
        const candidate = enabled[(here + step + enabled.length) % enabled.length];
        if (candidate?.textContent?.trim().toLocaleLowerCase("de").startsWith(typed)) {
          go(candidate);
          return;
        }
      }
    }
  }

  // Focus that leaves for another control closes it; focus that goes to the
  // trigger (ours, after a choice) or to nowhere (a window change) does not.
  function onBlur(event: ReactFocusEvent<HTMLUListElement>) {
    const next = event.relatedTarget;
    if (next instanceof Node && !event.currentTarget.contains(next) && !element?.contains(next)) {
      finish({ reason: "outside", focusWithin: false });
    }
  }

  const choose = (item: MenuItem) => finish({ reason: "select", item, focusWithin: true });
  const withIconColumn = items.some((item) => item.icon !== undefined);

  return createPortal(
    <ul
      ref={layerRef}
      id={id}
      role="menu"
      aria-label={label}
      tabIndex={-1}
      className={styles.menu}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      // Clicks here are the portal's own; whatever the menu hangs under (a
      // table row that opens its record) must not act on them.
      onClick={(event) => event.stopPropagation()}
    >
      {heading !== undefined ? (
        <li role="none" aria-hidden="true" className={styles.heading}>
          {heading}
        </li>
      ) : null}
      {items.map((item, index) => {
        const hintId = item.hint !== undefined ? `${hintPrefix}-${item.key}` : undefined;
        // The hint stands inside the item, so the item names itself by its
        // label alone and is described by the hint (and by what it points at).
        const described = [hintId, item.describedBy].filter(Boolean).join(" ") || undefined;
        const own = { "aria-label": hintId !== undefined ? item.label : undefined, "aria-describedby": described };
        const content = (
          <>
            {item.icon !== undefined ? (
              iconColumn(item.icon)
            ) : withIconColumn ? (
              <span aria-hidden="true" className={`${styles.icon} ${styles.iconSlot}`} data-icon-slot="" />
            ) : null}
            {hintId !== undefined ? (
              <span className={styles.text}>
                <span>{item.label}</span>
                <span id={hintId} className={styles.hint}>
                  {item.hint}
                </span>
              </span>
            ) : (
              item.label
            )}
          </>
        );
        return (
          <Fragment key={item.key}>
            {item.separatorBefore && index > 0 ? <li role="separator" className={styles.separator} /> : null}
            <li role="none">
              {item.disabled ? (
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  aria-disabled="true"
                  {...own}
                  className={styles.item}
                  // A press on it must not take the focus off the item that has it.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => event.preventDefault()}
                >
                  {content}
                </button>
              ) : item.href !== undefined && item.internal ? (
                <Link role="menuitem" tabIndex={-1} to={item.href} {...own} className={styles.item} onClick={() => choose(item)}>
                  {content}
                </Link>
              ) : item.href !== undefined ? (
                <a
                  role="menuitem"
                  tabIndex={-1}
                  {...own}
                  href={item.href}
                  download={item.download}
                  className={styles.item}
                  onClick={() => choose(item)}
                >
                  {content}
                </a>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  {...own}
                  className={styles.item}
                  onClick={() => choose(item)}
                >
                  {content}
                </button>
              )}
            </li>
          </Fragment>
        );
      })}
    </ul>,
    container,
  );
}

/**
 * The app's one menu (BW-8): a button that opens a list of actions or ways
 * to somewhere — the register row's „Weitere Aktionen", the record entry's
 * menu. ARIA menu button.
 *
 * Opens by click, Enter or Space (the button's own click), or ArrowDown —
 * ArrowUp opens on the last item — and the focus goes to the first enabled
 * item. ↑/↓ wander the enabled items round, Home/End jump, a letter jumps to
 * the next item that begins with it. Escape closes back on the trigger; Tab
 * closes back on it and is held, so the next Tab leaves from the trigger.
 * Choosing an item closes the menu, gives the focus to the trigger, and only
 * then runs `onSelect` — a dialog it opens takes the trigger as its opener,
 * and a button's `onSelect` finds the menu already gone from the document.
 * A press beside the menu closes it without taking the focus (the person
 * clicked elsewhere) and spends the click of that press — unless it fell on
 * another menu's trigger — so a register row does not open from the press
 * that only closed its menu. A scroll anywhere and a resize close it, with
 * the focus on the trigger if it was in the menu.
 *
 * In a portal: out of a table whose frame clips (`overflow: clip`), and into
 * the nearest dialog's box when the trigger sits in one — a `Dialog` makes
 * `#root` inert and a layer outside its box would be out of reach.
 * `position: fixed`, so it closes on a scroll instead of floating away from
 * what it belongs to.
 *
 * No items, no menu: it renders nothing.
 */
export function Menu({ label, heading, items, trigger, describedBy, align = "end", size = "m", onClosed }: MenuProps) {
  const menuId = useId();
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [opened, setOpened] = useState<"first" | "last" | null>(null);
  const isOpen = opened !== null;

  const closedRef = useRef(onClosed);
  useEffect(() => {
    closedRef.current = onClosed;
  }, [onClosed]);
  useEffect(() => {
    if (!isOpen) return undefined;
    return () => closedRef.current?.();
  }, [isOpen]);

  function onLayerClose({ reason, item, focusWithin }: LayerClose) {
    // A press beside the menu leaves the focus to the person; a scroll or a
    // resize takes the layer from under a focused item, and the focus goes
    // to the trigger rather than to the body.
    const toTrigger = reason === "escape" || reason === "tab" || reason === "select" || (focusWithin && (reason === "scroll" || reason === "resize"));
    if (reason === "select" && item?.href === undefined) {
      // A button's `onSelect` runs with the menu gone. A link is left its
      // element until the browser has followed it.
      flushSync(() => setOpened(null));
    } else {
      setOpened(null);
    }
    // A scroll that closes the menu must not be answered by a focus that scrolls the page back.
    if (toTrigger) anchor?.focus(reason === "scroll" || reason === "resize" ? { preventScroll: true } : undefined);
    if (reason === "select") item?.onSelect?.();
  }

  const props: TriggerProps = {
    ref: setAnchor,
    "aria-haspopup": "menu",
    "aria-expanded": isOpen,
    "aria-controls": isOpen ? menuId : undefined,
    "aria-describedby": describedBy,
    onClick: () => {
      if (isOpen) {
        setOpened(null);
        anchor?.focus();
      } else {
        setOpened("first");
      }
    },
    onKeyDown: (event) => {
      if (isOpen) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setOpened("first");
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setOpened("last");
      }
    },
    "data-menu-trigger": "",
  };

  if (items.length === 0) return null;

  return (
    <>
      {trigger !== undefined ? (
        trigger(props)
      ) : (
        <button
          type="button"
          className={size === "s" ? `${styles.trigger} ${styles.triggerSmall}` : styles.trigger}
          aria-label={label}
          {...props}
        >
          <MoreIcon />
        </button>
      )}
      {opened !== null && anchor !== null ? (
        <Layer
          id={menuId}
          label={label}
          heading={heading}
          items={items}
          container={floatingContainer(anchor)}
          anchor={anchor}
          side="below"
          align={align}
          focus={opened}
          swallowOutsideClick
          onClose={onLayerClose}
        />
      ) : null}
    </>
  );
}

interface ContextMenuProps {
  label: string;
  items: readonly MenuItem[];
  /** The pointer's point in viewport coordinates — or the centre of what the keyboard opened it on. */
  at: { x: number; y: number };
  /** The caller unmounts the menu in answer. */
  onClose: (reason: "escape" | "select" | "outside" | "tab") => void;
}

/**
 * The same menu, opened where a person pointed (right-click, Shift+F10)
 * instead of hanging on a trigger: it grows from the point, is pulled in from
 * the viewport's edges, and flips above the point at the bottom.
 *
 * Opens with the focus on the first enabled item and reports how it ended —
 * `escape`, `tab`, `select` or `outside` (a press beside it, a scroll, a
 * resize). On the first three the focus goes back to what had it when the
 * menu opened, before `onClose` runs, so a caller that knows a better place
 * (the wall that was clicked) simply moves it there. A press beside it
 * closes it and nothing more: the click that follows belongs to whatever is
 * under it. The caller unmounts it in `onClose`; for `select`, `onSelect`
 * runs right after.
 */
export function ContextMenu({ label, items, at, onClose }: ContextMenuProps) {
  // Read once, during the first render — before the focus moves into the menu.
  const [opener] = useState(() => {
    const active = document.activeElement;
    return active instanceof HTMLElement && active !== document.body ? active : null;
  });

  function onLayerClose({ reason, item, focusWithin }: LayerClose) {
    const ended = reason === "scroll" || reason === "resize" ? "outside" : reason;
    if ((ended !== "outside" || focusWithin) && opener?.isConnected) {
      opener.focus(reason === "scroll" || reason === "resize" ? { preventScroll: true } : undefined);
    }
    onClose(ended);
    if (ended === "select") item?.onSelect?.();
  }

  return (
    <Layer
      label={label}
      items={items}
      container={floatingContainer(opener)}
      anchor={at}
      side="below"
      align="start"
      focus="first"
      swallowOutsideClick={false}
      onClose={onLayerClose}
    />
  );
}
